import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Body2, SkeletonLoader, Table, Tbody, Thead } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { useAccessLogs } from "~/repository/useAccessLogs.js";
import { FormatLocalDate, FormatLocalTime } from "../Formatters/index.jsx";

const AccessLogs = ({ objectType, objectId }) => {
  const { t } = useTranslation();

  const { data, isLoading: loading } = useAccessLogs(objectType, objectId);

  const accessLogs = useMemo(() => {
    if (data && data.length > 0) {
      return data.reduce((acc, log) => {
        const key = moment.unix(log.accessed_on).startOf("day").unix();
        if (!(key in acc)) {
          acc[key] = [];
        }
        acc[key].push(log);
        return acc;
      }, {});
    }
  }, [data]);

  const renderPlaceholders = useMemo(() => {
    const placeholders = Array(9)
      .fill({})
      .map((_, index) => {
        return (
          <tr
            key={index}
            className="tw-w-full"
          >
            <td
              className="tw-h-7  tw-py-2 tw-pl-1"
              style={{ borderRadius: "10px 0 0 10px" }}
            >
              <SkeletonLoader data-testid="linear-loader" />
            </td>
            <td className="tw-h-7  tw-py-2">
              <SkeletonLoader className="" />
            </td>
            <td className="tw-h-7  tw-py-2 tw-w-28">
              <SkeletonLoader className="tw-w-full" />
            </td>
            <td
              className="tw-h-7  tw-py-2 tw-pr-1 tw-w-40"
              style={{ borderRadius: " 0 10px  10px 0" }}
            >
              <SkeletonLoader className="tw-w-full" />
            </td>
          </tr>
        );
      });

    return <Tbody className="tw-w-full">{placeholders}</Tbody>;
  }, []);

  const groupingDates = useMemo(() => {
    if (!accessLogs) {
      return [];
    }
    return Object.keys(accessLogs).sort((a, b) => {
      return Number(a) > Number(b) ? -1 : 1;
    });
  }, [accessLogs]);

  if (loading) {
    return (
      <Table
        className="tw-w-full tw-m-0 before:tw-shadow-none"
        striped
      >
        {renderPlaceholders}
      </Table>
    );
  }

  return (
    <>
      {groupingDates.length > 0 ? (
        groupingDates.map((groupingDate, index) => {
          return (
            <Table
              key={`group-${groupingDate}`}
              striped
              wrapperClassName="tw-m-0 before:tw-shadow-none"
              className="tw-min-w-[460px]"
            >
              <Thead
                color="secondary"
                weight="bold"
              >
                <tr className="">
                  <th
                    colSpan={index > 0 ? 4 : undefined}
                    className="tw-p-0 tw-py-2 tw-pl-1 tw-w-20"
                  >
                    <Body2
                      color="secondary"
                      weight="bold"
                      className="tw-uppercase"
                    >
                      <FormatLocalDate
                        value={parseInt(groupingDate)}
                        long={false}
                      />
                    </Body2>
                  </th>
                  {index === 0 && (
                    <>
                      <th className="tw-p-0 tw-py-2 tw-w-24">
                        <Body2
                          color="secondary"
                          weight="bold"
                          className="tw-uppercase"
                        >
                          {t("action")}
                        </Body2>
                      </th>
                      <th className="tw-p-0 tw-py-2 tw-max-w-36">
                        <Body2
                          color="secondary"
                          weight="bold"
                          className="tw-uppercase"
                        >
                          {t("user")}
                        </Body2>
                      </th>
                      <th className="tw-p-0 tw-py-2 tw-pr-1 tw-w-36">
                        <Body2
                          color="secondary"
                          weight="bold"
                          align="right"
                          className="tw-uppercase"
                        >
                          {t("ip address")}
                        </Body2>
                      </th>
                    </>
                  )}
                </tr>
              </Thead>
              <Tbody className="tw-border-collapse tw-w-full tw--mx-1 tw-text-sm tw-text-theme-700 tw-leading-none">
                {accessLogs[groupingDate].map((log, index) => {
                  return (
                    <tr
                      key={`group-table-row-${index}`}
                      className="tw-py-2 tw-h-7"
                    >
                      <td
                        className="tw-p-0 tw-pl-1 tw-w-20"
                        style={{ borderRadius: "10px 0 0 10px" }}
                      >
                        <Body2
                          letterSpacing="regular"
                          color="tertiary"
                          className="tw-truncate"
                        >
                          <FormatLocalTime value={log.accessed_on} />
                        </Body2>
                      </td>
                      <td className="tw-p-0 tw-w-24">
                        <Body2
                          letterSpacing="regular"
                          color="tertiary"
                          className="tw-w-24 tw-truncate"
                        >
                          {log.action === "access" ? t("Access") : t("Download")}
                        </Body2>
                      </td>
                      <td className="tw-p-0 tw-w-36">
                        <Body2
                          letterSpacing="regular"
                          color="tertiary "
                          className="tw-truncate tw-w-36"
                        >
                          {log.name}
                        </Body2>
                      </td>
                      <td
                        className=" tw-text-theme-600 tw-p-0 tw-pr-1 tw-w-36"
                        style={{ borderRadius: " 0 10px  10px 0" }}
                      >
                        <Body2
                          letterSpacing="regular"
                          color="tertiary"
                          align="right"
                          className="tw-truncate tw-w-36"
                        >
                          {log.address}
                        </Body2>
                      </td>
                    </tr>
                  );
                })}
              </Tbody>
            </Table>
          );
        })
      ) : (
        <p>{t("There are no access logs for this object")}</p>
      )}
    </>
  );
};

AccessLogs.propTypes = {
  objectType: PropTypes.string.isRequired,
  objectId: PropTypes.number.isRequired,
};

export default AccessLogs;
